import { cs } from '../../../lib/currency';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../../components/ui/alert-dialog';
import { Checkbox } from '../../../components/ui/checkbox';
import { Textarea } from '../../../components/ui/textarea';
import { Trash2, AlertTriangle, Loader2 } from 'lucide-react';

export const DeleteRegistrationDialog = ({
  open, onOpenChange, registrationToDelete, registrationDetails,
  loadingRegistrationDetails, deletingRegistration, refundCheckbox, setRefundCheckbox,
  deleteNotes, setDeleteNotes, onConfirm
}) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle className="flex items-center gap-2">
          <Trash2 className="h-5 w-5 text-red-600" />
          {registrationToDelete?.payment_status === 'paid' ? 'Cancel Registration' : 'Delete Registration'}
        </AlertDialogTitle>
        <AlertDialogDescription asChild>
          <div className="space-y-4">
            {loadingRegistrationDetails ? (
              <div className="flex items-center justify-center py-4"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : (
              <>
                <p>
                  {registrationToDelete?.payment_status === 'paid'
                    ? 'This registration has been paid. The record will be kept for audit (status set to cancelled / unregistered).'
                    : 'Are you sure you want to remove this registration? It will be soft-archived — the record is preserved for audit and can be reinstated later.'}
                </p>
                {registrationToDelete && (
                  <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                    <p><strong>Player:</strong> {registrationToDelete.player_name}</p>
                    <p><strong>Parent:</strong> {registrationToDelete.parent_name}</p>
                    <p><strong>Category:</strong> {registrationToDelete.age_category?.toUpperCase()}</p>
                    <p><strong>Status:</strong> {registrationToDelete.status}</p>
                    {registrationToDelete.payment_status === 'paid' && (
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <p className="text-amber-600 font-medium mb-2">This registration has been paid</p>
                        <p><strong>Amount Paid:</strong> {cs()}{registrationToDelete.payment_amount?.toFixed(2) || registrationDetails?.registration?.payment_amount?.toFixed(2) || '0.00'}</p>
                        {registrationDetails?.can_refund && (
                          <div className="flex items-center gap-2 mt-3">
                            <Checkbox id="refund-checkbox" checked={refundCheckbox} onCheckedChange={(checked) => setRefundCheckbox(checked)} data-testid="refund-checkbox" />
                            <label htmlFor="refund-checkbox" className="text-sm cursor-pointer">Process refund via Stripe</label>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {registrationToDelete?.payment_status === 'paid' && !refundCheckbox && (
                  <div className="border-2 border-red-300 bg-red-50 rounded-lg p-3 mt-3">
                    <div className="flex items-start gap-2 text-red-700">
                      <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold">Warning: No Refund Selected</p>
                        <p className="text-sm mt-1">This player has paid <strong>{cs()}{registrationToDelete?.payment_amount?.toFixed(2) || '0.00'}</strong>. Cancelling without a refund will keep the payment without issuing any reimbursement.</p>
                      </div>
                    </div>
                  </div>
                )}
                <p className="text-sm text-slate-500 mt-3">
                  {registrationToDelete?.payment_status === 'paid'
                    ? 'The player will be removed from any team and marked as unregistered. The registration record is preserved for audit and refund traceability.'
                    : 'The registration will be archived — player removed from any team, RSVPs blocked. You can reinstate it from the Cancelled tab if needed.'}
                </p>

                <div className="mt-4 pt-3 border-t border-slate-200">
                  <label htmlFor="delete-notes" className="block text-sm font-medium text-slate-700 mb-1">
                    Reason / Notes for Parent <span className="text-slate-400 font-normal">(included in the withdrawal email)</span>
                  </label>
                  <Textarea
                    id="delete-notes"
                    placeholder="e.g. Roster needed re-balancing across age categories — your child has been re-enrolled in U-15 (we'll send a fresh registration link separately)."
                    value={deleteNotes || ''}
                    onChange={(e) => setDeleteNotes?.(e.target.value)}
                    rows={3}
                    className="text-sm"
                    data-testid="delete-registration-notes-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Sent verbatim to the parent in the withdrawal confirmation email. Leave blank to send just the standard notice.
                  </p>
                </div>
              </>
            )}
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel disabled={deletingRegistration}>Cancel</AlertDialogCancel>
        <AlertDialogAction
          onClick={onConfirm} disabled={deletingRegistration || loadingRegistrationDetails}
          className={registrationToDelete?.payment_status === 'paid' && refundCheckbox ? "bg-amber-600 hover:bg-amber-700" : "bg-red-600 hover:bg-red-700"}
          data-testid="confirm-delete-registration-btn"
        >
          {deletingRegistration ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />{registrationToDelete?.payment_status === 'paid' ? 'Cancelling...' : 'Deleting...'}</>) : registrationToDelete?.payment_status === 'paid' && refundCheckbox ? 'Cancel & Refund' : registrationToDelete?.payment_status === 'paid' ? 'Cancel Registration' : 'Delete Registration'}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
